import { ref } from "vue";
import { useRouter } from "vue-router";
import { useConversationStore } from "../stores";
import { conversationService } from "../services/conversation.service";
/**
 * Handles PRE_BOOKING and POST_BOOKING conversation creation flows.
 * Performs duplicate detection before creating and redirects to the conversation.
 */
export function useConversationCreation() {
    const router = useRouter();
    const store = useConversationStore();
    const isCreating = ref(false);
    async function redirectToConversation(conversationId) {
        await router.push({
            path: "/admin/conversations",
            query: { conversationId },
        });
    }
    async function createPreBooking(params) {
        isCreating.value = true;
        try {
            const duplicate = conversationService.findDuplicate(store.conversations, {
                serviceId: params.serviceId,
            });
            if (duplicate) {
                await redirectToConversation(duplicate.id);
                return;
            }
            const payload = {
                conversation_type: "PRE_BOOKING",
                service_id: params.serviceId,
            };
            const conversation = await store.createConversation(payload);
            await redirectToConversation(conversation.id);
        }
        finally {
            isCreating.value = false;
        }
    }
    async function createPostBooking(params) {
        isCreating.value = true;
        try {
            const duplicate = conversationService.findDuplicate(store.conversations, {
                serviceId: params.serviceId,
                itineraryId: params.itineraryId,
                bookingId: params.bookingId,
                slotId: params.slotId,
            });
            if (duplicate) {
                await redirectToConversation(duplicate.id);
                return;
            }
            const payload = {
                conversation_type: "POST_BOOKING",
                provider_id: params.providerId,
                service_id: params.serviceId,
                itinerary_id: params.itineraryId,
                booking_id: params.bookingId,
                slot_id: params.slotId,
            };
            const conversation = await store.createConversation(payload);
            await redirectToConversation(conversation.id);
        }
        finally {
            isCreating.value = false;
        }
    }
    /**
     * Entry-point helper used by ItineraryCard and BookingCard.
     * If itineraryId + bookingId/slotId are present → POST_BOOKING; otherwise PRE_BOOKING.
     */
    async function findOrRedirect(params) {
        if (params.itineraryId && (params.bookingId || params.slotId)) {
            await createPostBooking({
                serviceId: params.serviceId,
                providerId: params.providerId,
                itineraryId: params.itineraryId,
                bookingId: params.bookingId,
                slotId: params.slotId,
            });
        }
        else {
            await createPreBooking({
                serviceId: params.serviceId,
                providerId: params.providerId,
            });
        }
    }
    return { isCreating, createPreBooking, createPostBooking, findOrRedirect };
}
